import { useCallback, useEffect, useRef, useState } from 'react'
import { Monitor, Plus, Trash2, RefreshCw, Play, Wifi, WifiOff, TestTube2, Pencil, X, Check, Music2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { obsAPI, overlayAPI } from '@/lib/api'
import { openMIDI, listInputs, attachInputListener, eventKey, keyLabel } from '@/lib/tournamentsMidi'

/* MIDI-mapping: één localStorage-blob {[bindingKey]: {obsId, scene}}.
   bindingKey is `note:ch:num` of `cc:ch:num`. Luisteraar: dezelfde APC-input
   die het bestaande ScreenMidiPanel al selecteerde (`screens-midi-apc-in`).
   Als er nog niets gekozen is, valt de listener terug op de eerste input. */
const MAP_KEY = 'obs-midi-bindings'
const APC_IN_KEY = 'screens-midi-apc-in'
const leesMap  = () => { try { return JSON.parse(localStorage.getItem(MAP_KEY) || '{}') } catch { return {} } }
const schrijfMap = (m) => { try { localStorage.setItem(MAP_KEY, JSON.stringify(m)) } catch {} }

/* OBS-instances panel voor schermregie.
   - Beheer één of meer OBS-PC's, test connectie, live scene-picker.
   - Inline edit-form (geen window.prompt-popups).
   - Password blijft server-side; toont als '***'. Leeg laten bij bewerken
     = ongewijzigd; expliciet wissen via een aparte knop. */

const EMPTY = { label: '', host: '', port: 4455, password: '' }

export default function OBSInstancesPanel() {
  const [instances, setInstances] = useState([])
  const [channels, setChannels]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [busy, setBusy]           = useState(null)
  const [scenes, setScenes]       = useState({})
  const [editId, setEditId]       = useState(null)   // null / 'new' / <id>
  const [form, setForm]           = useState(EMPTY)
  const [pwEdit, setPwEdit]       = useState(false)  // wachtwoord ook wijzigen?
  const [bindings, setBindings]   = useState(() => leesMap())
  const [leerMode, setLeerMode]   = useState(null)   // {obsId, scene} tijdens leer-flow
  const [midiOn, setMidiOn]       = useState(false)
  const [midiInput, setMidiInput] = useState('')
  const bindingsRef = useRef(bindings)
  useEffect(() => { bindingsRef.current = bindings }, [bindings])
  const leerRef = useRef(null)
  useEffect(() => { leerRef.current = leerMode }, [leerMode])

  const laad = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([obsAPI.list(), overlayAPI.channels()])
      setInstances(r.data?.instances || [])
      setChannels(c.data?.channels || [])
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { laad() }, [laad])

  useEffect(() => {
    const t = setInterval(async () => {
      for (const inst of instances) {
        if (inst.live_status?.connected) {
          try {
            const r = await obsAPI.scenes(inst.id)
            if (r.data?.ok) setScenes(s => ({ ...s, [inst.id]: r.data }))
          } catch {}
        }
      }
    }, 5000)
    return () => clearInterval(t)
  }, [instances])

  // MIDI-listener: dezelfde APC-input als ScreenMidiPanel of eerste beschikbaar
  useEffect(() => {
    let cancel = null
    ;(async () => {
      try {
        const access = await openMIDI()
        const inputs = listInputs(access)
        if (!inputs.length) return
        const gewenst = localStorage.getItem(APC_IN_KEY) || ''
        const gekozen = inputs.find(i => i.id === gewenst) || inputs[0]
        setMidiOn(true)
        setMidiInput(gekozen.name)
        cancel = attachInputListener(gekozen, async (evt) => {
          const key = eventKey(evt)
          if (!key) return
          // Leer-modus: eerstvolgende note-on koppelt aan pending scene
          if (leerRef.current && evt.type === 0x90) {
            const nw = { ...leesMap(), [key]: leerRef.current }
            schrijfMap(nw); setBindings(nw); setLeerMode(null)
            return
          }
          // Trigger scene bij binding-match
          const bind = bindingsRef.current[key]
          if (bind && evt.type === 0x90) {
            try {
              await obsAPI.setScene(bind.obsId, bind.scene)
              const s = await obsAPI.scenes(bind.obsId)
              if (s.data?.ok) setScenes(x => ({ ...x, [bind.obsId]: s.data }))
            } catch {}
          }
        })
      } catch (e) {
        // Geen MIDI beschikbaar (bv. Firefox) — stil, MIDI-knopjes verdwijnen
      }
    })()
    return () => { if (cancel) cancel() }
  }, [])

  const bindingFor = (obsId, scene) => {
    return Object.entries(bindings).find(([, v]) => v.obsId === obsId && v.scene === scene)?.[0] || null
  }
  const startLeer = (obsId, scene) => setLeerMode({ obsId, scene })
  const wisBinding = (bindingKey) => {
    const nw = { ...bindings }; delete nw[bindingKey]
    schrijfMap(nw); setBindings(nw)
  }

  const openNieuw = () => {
    setForm({ label: '', host: '192.168.129.', port: 4455, password: '' })
    setPwEdit(true)
    setEditId('new')
  }
  const openBewerk = (inst) => {
    setForm({ label: inst.label, host: inst.host, port: inst.port, password: '' })
    setPwEdit(false)
    setEditId(inst.id)
  }
  const sluit = () => { setEditId(null); setForm(EMPTY); setPwEdit(false) }

  const opslaan = async () => {
    if (!form.label.trim() || !form.host.trim()) { alert('Label + host verplicht'); return }
    setBusy('save')
    try {
      const body = { label: form.label.trim(), host: form.host.trim(), port: Number(form.port) || 4455 }
      if (editId === 'new' || pwEdit) body.password = form.password
      if (editId === 'new') {
        await obsAPI.create({ ...body, enabled: true })
      } else {
        await obsAPI.update(editId, body)
      }
      sluit(); await laad()
    } catch (e) { alert('Opslaan mislukt: ' + (e.response?.data?.error || e.message)) }
    finally { setBusy(null) }
  }

  const test = async (inst) => {
    setBusy(`test-${inst.id}`)
    try {
      const r = await obsAPI.test({ id: inst.id })
      const d = r.data
      if (d.ok) alert(`✓ Verbonden — OBS ${d.obsVersion}, WebSocket ${d.wsVersion}\n${d.sceneCount} scene(s), huidige: "${d.currentScene}"`)
      else alert(`✗ Test mislukt:\n${d.error}`)
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)) }
    finally { setBusy(null); laad() }
  }

  const reconnect = async (inst) => {
    setBusy(`rc-${inst.id}`)
    try { await obsAPI.reconnect(inst.id) } catch {}
    finally { setBusy(null); laad() }
  }

  const koppel = async (inst, channelId) => {
    setBusy(`link-${inst.id}`)
    try { await obsAPI.update(inst.id, { overlay_channel_id: channelId || null }); await laad() }
    finally { setBusy(null) }
  }

  const toggle = async (inst) => {
    setBusy(`en-${inst.id}`)
    try { await obsAPI.update(inst.id, { enabled: !inst.enabled }); await laad() }
    finally { setBusy(null) }
  }

  const verwijder = async (inst) => {
    if (!window.confirm(`OBS-instance "${inst.label}" verwijderen?`)) return
    setBusy(`del-${inst.id}`)
    try { await obsAPI.remove(inst.id); await laad() }
    finally { setBusy(null) }
  }

  const scene = async (inst, name) => {
    setBusy(`sc-${inst.id}`)
    try {
      const r = await obsAPI.setScene(inst.id, name)
      if (!r.data?.ok) alert(r.data?.error || 'Wisselen mislukt')
      const s = await obsAPI.scenes(inst.id)
      if (s.data?.ok) setScenes(x => ({ ...x, [inst.id]: s.data }))
    } finally { setBusy(null) }
  }

  const EditForm = ({ isNew }) => (
    <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="text-muted-foreground">Label</span>
          <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Beamer 1" />
        </label>
        <label className="text-xs">
          <span className="text-muted-foreground">IP-adres of hostnaam</span>
          <Input value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} placeholder="192.168.129.76" />
        </label>
        <label className="text-xs">
          <span className="text-muted-foreground">Poort</span>
          <Input type="number" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} />
        </label>
        <label className="text-xs">
          <span className="text-muted-foreground flex items-center gap-2">
            Wachtwoord
            {!isNew && (
              <label className="inline-flex items-center gap-1 text-xs font-normal">
                <input type="checkbox" checked={pwEdit} onChange={e => setPwEdit(e.target.checked)} />
                wijzigen
              </label>
            )}
          </span>
          <Input
            type="password"
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder={isNew ? 'uit OBS → Tools → WebSocket Server Settings' : (pwEdit ? '(leeg = wissen)' : 'ongewijzigd')}
            disabled={!isNew && !pwEdit}
          />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={sluit} disabled={busy === 'save'}>
          <X className="w-4 h-4 mr-1" /> Annuleren
        </Button>
        <Button size="sm" onClick={opslaan} disabled={busy === 'save'}>
          <Check className="w-4 h-4 mr-1" /> {isNew ? 'Aanmaken' : 'Opslaan'}
        </Button>
      </div>
    </div>
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Monitor className="w-5 h-5" /> OBS-verbindingen</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={laad} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={openNieuw} disabled={editId !== null}>
            <Plus className="w-4 h-4 mr-1" /> Nieuwe OBS
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {midiOn && (
          <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
            <Music2 className="w-3 h-3" />
            MIDI-input: <b>{midiInput}</b>. Klik het MIDI-icoontje naast een scene om te koppelen.
            {leerMode && <span className="text-amber-500 animate-pulse ml-2">Wachten op MIDI-knop…</span>}
          </div>
        )}
        {editId === 'new' && <div className="mb-3"><EditForm isNew /></div>}

        {loading ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : instances.length === 0 && editId !== 'new' ? (
          <p className="text-sm text-muted-foreground">
            Geen OBS-instances gekoppeld. Klik "Nieuwe OBS" en vul IP + WebSocket-wachtwoord in.
            OBS zelf moet Tools → WebSocket Server Settings aan hebben.
          </p>
        ) : (
          <div className="space-y-3">
            {instances.map(inst => {
              const st = inst.live_status || {}
              const scData = scenes[inst.id]
              const isEditing = editId === inst.id
              return (
                <div key={inst.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      {st.connected
                        ? <Wifi className="w-4 h-4 text-green-500" />
                        : <WifiOff className="w-4 h-4 text-red-500" />}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{inst.label}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {inst.host}:{inst.port} — {inst.has_password ? 'auth' : 'geen wachtwoord'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => test(inst)} disabled={busy === `test-${inst.id}`}>
                        <TestTube2 className="w-3 h-3 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reconnect(inst)} disabled={busy === `rc-${inst.id}`}>
                        <RefreshCw className={`w-3 h-3 mr-1 ${busy === `rc-${inst.id}` ? 'animate-spin' : ''}`} /> Reconnect
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openBewerk(inst)} disabled={editId !== null}>
                        <Pencil className="w-3 h-3 mr-1" /> Bewerk
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(inst)}>
                        {inst.enabled ? 'Uit' : 'Aan'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => verwijder(inst)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {!st.connected && st.lastError && (
                    <div className="text-xs text-red-500">Fout: {st.lastError}</div>
                  )}

                  {isEditing && <EditForm isNew={false} />}

                  {/* Kanaal-koppeling */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Kanaal-koppeling:</span>
                    <select
                      className="border rounded px-2 py-1 bg-background"
                      value={inst.overlay_channel_id || ''}
                      onChange={e => koppel(inst, e.target.value ? Number(e.target.value) : null)}
                      disabled={busy === `link-${inst.id}`}
                    >
                      <option value="">— geen —</option>
                      {channels.map(c => <option key={c.id} value={c.id}>{c.name} ({c.slug})</option>)}
                    </select>
                  </div>

                  {/* Scenes-lijst */}
                  {st.connected && scData && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">
                        Scenes {scData.current && <>— huidige: <b>{scData.current}</b></>}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {scData.scenes.map(s => {
                          const bkey = bindingFor(inst.id, s)
                          const isLeren = leerMode?.obsId === inst.id && leerMode?.scene === s
                          return (
                            <div key={s} className="inline-flex items-center gap-0">
                              <Button
                                size="sm"
                                variant={s === scData.current ? 'default' : 'outline'}
                                onClick={() => scene(inst, s)}
                                disabled={busy === `sc-${inst.id}`}
                                className="rounded-r-none"
                              >
                                <Play className="w-3 h-3 mr-1" /> {s}
                              </Button>
                              {midiOn && (
                                <Button
                                  size="sm"
                                  variant={isLeren ? 'default' : (bkey ? 'secondary' : 'outline')}
                                  onClick={() => bkey ? wisBinding(bkey) : startLeer(inst.id, s)}
                                  title={
                                    isLeren ? 'Wachten op MIDI-knop… (klik om te stoppen)'
                                    : bkey ? `MIDI ${keyLabel(bkey)} → klik om los te koppelen`
                                    : 'Klik en druk dan op een MIDI-knop om te koppelen'
                                  }
                                  className="rounded-l-none border-l-0 px-2"
                                >
                                  <Music2 className={`w-3 h-3 ${isLeren ? 'animate-pulse' : ''}`} />
                                  {bkey && <span className="ml-1 text-[10px]">{bkey.split(':')[2]}</span>}
                                </Button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
